function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findByLabel(text) {
	const labels = document.querySelectorAll('label, .MuiFormLabel-root, .MuiInputLabel-root')
	for (const label of labels) {
		if (label.textContent.toLowerCase().includes(text.toLowerCase())) {
			return label.closest('.MuiFormControl-root') || label.closest('.MuiGrid-item') || label.parentElement
		}
	}
	return null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'chart-mixed',
		title: ES ? 'Bienvenido a Analíticas de Búsqueda' : 'Welcome to Search Analytics',
		description: ES
			? 'Este panel muestra cómo se está utilizando el motor de búsqueda personalizado de Europcar. Consulta los volúmenes de búsqueda, observa las tendencias diarias comparadas con el mes anterior y desglosa la actividad por empresa, usuario y sitio.'
			: 'This dashboard shows how Europcar\'s custom search engine is being used. Track search volumes, see daily trends compared to the previous month, and break down activity by company, user, and site.',
	},
	{
		type: 'field',
		findTarget: () => {
			const dateLabel = findByLabel('Start date') || findByLabel('Date')
			if (dateLabel) {
				const grid = dateLabel.closest('.MuiGrid-item')?.parentElement
				if (grid) return grid
			}
			const autocompletes = document.querySelectorAll('.MuiAutocomplete-root, .MuiFormControl-root')
			if (autocompletes.length >= 4) {
				const parent = autocompletes[0].closest('.MuiGrid-container')
				if (parent) return parent
			}
			return null
		},
		icon: 'filter',
		title: ES ? 'Filtros' : 'Filters',
		description: ES
			? 'Filtra el panel por rango de fechas, empresa, usuario y sitio. Los datos se actualizan automáticamente al cambiar cualquier filtro, sin necesidad de pulsar un botón de búsqueda. El rango de fechas predeterminado es los últimos 7 días.'
			: 'Filter the dashboard by date range, company, user, and site. Data updates automatically when you change any filter — no search button needed. The date range defaults to the last 7 days.',
		example: ES ? 'Rango de fechas | Empresa | Usuarios | Sitios' : 'Date Range | Company | Users | Sites',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('This week')
			if (el) {
				const card = el.closest('.MuiPaper-root') || el.closest('.MuiCard-root')
				if (card?.parentElement) {
					const parent = card.parentElement
					const children = parent.querySelectorAll('.MuiPaper-root, .MuiCard-root')
					if (children.length >= 3) return parent
				}
				return card
			}
			return null
		},
		icon: 'chart-simple',
		title: ES ? 'Tarjetas de Volumen de Búsqueda' : 'Search Volume Cards',
		description: ES
			? 'Tres tarjetas KPI que muestran el total de búsquedas: para el rango de fechas seleccionado, la semana actual y el mes actual. Los números se animan con un efecto de conteo cuando se cargan los datos.'
			: 'Three KPI cards showing total search counts: for the selected date range, the current week, and the current month. Numbers animate with a count-up effect when data loads.',
		example: ES ? 'Rango de fechas: 1.247 | Semana: 312 | Mes: 1.890' : 'Date Range: 1,247 | Week: 312 | Month: 1,890',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Daily searches vs Previous Month')
			if (el) {
				const container = el.closest('.MuiPaper-root') || el.closest('[id*="chart"]')?.parentElement || el.parentElement?.parentElement
				return container
			}
			const chart = document.querySelector('[id*="SearchAnalytics"]')
			return chart?.closest('.MuiPaper-root') || chart?.parentElement
		},
		icon: 'chart-column',
		title: ES ? 'Gráfico de Búsquedas Diarias' : 'Daily Searches Chart',
		description: ES
			? 'Gráfico de columnas que compara los volúmenes de búsqueda diarios con el mismo periodo del mes anterior. Dos series codificadas por color permiten detectar tendencias: ¿las búsquedas crecen, disminuyen o son estacionales? Pasa el ratón sobre cualquier barra para ver los conteos exactos.'
			: 'Column chart comparing daily search volumes against the same period last month. Two color-coded series let you spot trends — are searches growing, declining, or seasonal? Hover any bar for exact counts.',
		example: ES ? 'Azul = Periodo actual | Gris = Mes anterior' : 'Blue = Current period | Grey = Previous month',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Searches by Company')
			if (el) {
				const block = el.parentElement
				if (block) return block
			}
			return null
		},
		icon: 'building',
		title: ES ? 'Búsquedas por Empresa' : 'Searches by Company',
		description: ES
			? 'Un gráfico circular que muestra la distribución de búsquedas por empresa/país, junto con una tabla detallada debajo. Consulta de un vistazo qué mercados generan más volumen de búsqueda.'
			: 'A pie chart showing the distribution of searches across companies/countries, plus a detailed table below. See which markets are driving the most search volume at a glance.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Searches by User')
			if (el) {
				const block = el.parentElement
				if (block) return block
			}
			return null
		},
		icon: 'user',
		title: ES ? 'Búsquedas por Usuario' : 'Searches by User',
		description: ES
			? 'Muestra qué usuarios individuales realizan más búsquedas. El gráfico circular ofrece una visión rápida y la tabla lista cada usuario con su número exacto de búsquedas.'
			: 'Shows which individual users are performing the most searches. The pie chart gives a quick overview, and the table lists each user with their exact search count.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Searches by Site')
			if (el) {
				const block = el.parentElement
				if (block) return block
			}
			return null
		},
		icon: 'globe-pointer',
		title: ES ? 'Búsquedas por Sitio' : 'Searches by Site',
		description: ES
			? 'Desglosa la actividad de búsqueda por sitio web de alquiler. Permite entender qué sitios de la competencia se monitorizan con más frecuencia. Cada bloque incluye un gráfico circular para proporciones y una tabla con los números exactos.'
			: 'Breaks down search activity by rental website. Understand which competitor sites are being monitored most frequently. Each block includes a pie chart for proportions and a table for exact numbers.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Ajusta el rango de fechas y los filtros para explorar diferentes periodos. Todos los gráficos y tablas se actualizan automáticamente. Usa los desgloses por empresa, usuario y sitio para entender los patrones de búsqueda en tu organización.'
			: 'Adjust the date range and filters to explore different time periods. All charts and tables update automatically. Use the company, user, and site breakdowns to understand search patterns across your organization.',
	},
]
